import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { mdyDow, mdy } from "@/lib/dateFmt";
import { X, Save, ShieldCheck, AlertTriangle, Check } from "lucide-react";

/**
 * Admin-only override editor for an employee's availability.
 *
 * Props:
 *   open, onClose
 *   payPeriodId   — "pp-YYYY-MM-DD" or "mo-YYYY-MM"
 *   facilityId
 *   employee      — { id, name, days: [{date, codes}] }
 *   dateRange     — { start, end } ISO — inclusive window for this period
 *   onSaved()     — refetch after write
 *
 * Bypasses the min-requirement gate + "already submitted" guard on the
 * backend via POST /admin/availability. Used by admins to fix a stuck
 * submission or stand-in on behalf of an employee from the office.
 */
const SLOTS = [
  { code: "D", label: "Day",   cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { code: "E", label: "Eve",   cls: "bg-orange-100 text-orange-800 border-orange-300" },
  { code: "N", label: "Night", cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
];

function eachDay(startIso, endIso) {
  if (!startIso || !endIso) return [];
  const out = [];
  const s = parseISO(startIso);
  const e = parseISO(endIso);
  for (let d = new Date(s); d <= e; d.setDate(d.getDate() + 1)) {
    out.push(new Date(d).toISOString().slice(0, 10));
  }
  return out;
}

export default function AdminAvailabilityEditor({
  open, onClose, payPeriodId, facilityId, employee, dateRange, onSaved,
}) {
  const [rows, setRows] = useState({});
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState(null);
  // Assignments-in-window for THIS employee at the target facility, keyed by
  // ISO date. Powers the "already scheduled — file PTO" caution and the
  // < 14-day / < 24-hour notice caution on the manager side too.
  const [assignmentsByDate, setAssignmentsByDate] = useState({});

  useEffect(() => {
    if (!open) return;
    const seed = {};
    for (const iso of eachDay(dateRange?.start, dateRange?.end)) {
      seed[iso] = new Set();
    }
    for (const d of (employee?.days || [])) {
      if (seed[d.date] !== undefined) seed[d.date] = new Set(d.codes || []);
    }
    setRows(seed);
  }, [open, employee?.id, dateRange?.start, dateRange?.end]);

  // Pull facility minimum-availability rules so the manager can see, live,
  // whether this employee is meeting each requirement. Unlike the employee
  // view we never DISABLE save — managers are trusted to override — but we
  // still surface the counters + a caution row when a rule is short so the
  // manager knows exactly what they're signing off on.
  useEffect(() => {
    if (!open || !facilityId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/facilities/${facilityId}/settings`);
        if (!cancelled) setSettings(data || null);
      } catch { if (!cancelled) setSettings(null); }
    })();
    return () => { cancelled = true; };
  }, [open, facilityId]);

  useEffect(() => {
    if (!open || !facilityId || !employee?.id) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/partner-shifts`, {
          params: { facility_id: facilityId, employee_id: employee.id },
        });
        if (cancelled) return;
        const map = {};
        (data || []).forEach((a) => { map[a.date] = a; });
        setAssignmentsByDate(map);
      } catch { if (!cancelled) setAssignmentsByDate({}); }
    })();
    return () => { cancelled = true; };
  }, [open, facilityId, employee?.id]);

  const dates = useMemo(() => eachDay(dateRange?.start, dateRange?.end), [dateRange?.start, dateRange?.end]);

  // Live counters — matches the employee-side rollup so managers see the
  // exact numbers the employee would see if they opened the same period.
  const totals = useMemo(() => {
    let d = 0, e = 0, n = 0, w = 0, tot = 0;
    for (const iso of dates) {
      const codes = rows[iso] || new Set();
      if (codes.size === 0) continue;
      tot += 1;
      const dt = iso ? parseISO(iso) : null;
      if (dt && (dt.getDay() === 0 || dt.getDay() === 6)) w += 1;
      if (codes.has("D")) d += 1;
      if (codes.has("E")) e += 1;
      if (codes.has("N")) n += 1;
    }
    return { total_days: tot, day_shifts: d, evening_shifts: e, night_shifts: n, weekend_days: w };
  }, [rows, dates]);

  const RULES = useMemo(() => ([
    { key: "total_days",     label: "Total days",  need: Number(settings?.min_total_days     || 0), have: totals.total_days     },
    { key: "weekend_days",   label: "Weekends",    need: Number(settings?.min_weekend_days   || 0), have: totals.weekend_days   },
    { key: "day_shifts",     label: "Day (D)",     need: Number(settings?.min_day_shifts     || 0), have: totals.day_shifts     },
    { key: "evening_shifts", label: "Evening (E)", need: Number(settings?.min_evening_shifts || 0), have: totals.evening_shifts },
    { key: "night_shifts",   label: "Night (N)",   need: Number(settings?.min_night_shifts   || 0), have: totals.night_shifts   },
  ]), [settings, totals]);
  const shortRules = RULES.filter(r => r.need > 0 && r.have < r.need);
  const anyRuleSet = RULES.some(r => r.need > 0);

  // Codes that mean "already off" — no conflict if empty codes on that day.
  const OFF_DAY_CODES = new Set(["PTO", "P", "U", "H", "LOA", "L", "A", "B"]);
  const assignedConflicts = useMemo(() => {
    const out = [];
    for (const iso of dates) {
      const codes = rows[iso] || new Set();
      if (codes.size > 0) continue;
      const a = assignmentsByDate[iso];
      if (!a) continue;
      let code = a.shift_code;
      if (code === "P") code = a.prev_shift_code || code;
      if (!code || OFF_DAY_CODES.has(code)) continue;
      out.push({ date: iso, code });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, dates, assignmentsByDate]);

  // Solano = 24-hour notice, everyone else = 14 days.
  const noticeThresholdDays = facilityId === "solano" ? 1 : 14;
  const noticeCautions = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const out = [];
    for (const iso of dates) {
      const codes = rows[iso] || new Set();
      if (codes.size > 0) continue;
      const dt = parseISO(iso);
      const daysAhead = Math.round((dt - today) / (1000 * 60 * 60 * 24));
      if (daysAhead < 0) continue;
      if (daysAhead >= noticeThresholdDays) continue;
      if (assignedConflicts.find(b => b.date === iso)) continue;
      out.push({ date: iso, daysAhead });
    }
    return out;
  }, [rows, dates, noticeThresholdDays, assignedConflicts]);

  if (!open) return null;

  const toggle = (iso, code) => {
    setRows((prev) => {
      const s = new Set(prev[iso] || []);
      if (s.has(code)) s.delete(code); else s.add(code);
      return { ...prev, [iso]: s };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const days = Object.entries(rows).map(([date, codes]) => ({
        date, codes: [...codes],
      }));
      const { data } = await api.post("/admin/availability", {
        facility_id: facilityId,
        pay_period_id: payPeriodId,
        employee_id: employee.id,
        days,
      });
      toast.success(data?.was_existing ? "Availability updated" : "Availability submitted");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed to save");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4" data-testid="admin-avail-editor">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-lg font-semibold">
              <ShieldCheck size={18} className="text-fuchsia-600" />
              Edit availability — <span className="text-fuchsia-700">{employee?.name}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Manager override — you can save below the minimums (unlike the employee view). The rule counters below tell you if the submission would clear the facility's minimum requirements.
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {dates.map((iso) => {
              const codes = rows[iso] || new Set();
              return (
                <div key={iso} className="rounded-lg border border-slate-200 p-2 flex items-center justify-between gap-3" data-testid={`admin-avail-row-${iso}`}>
                  <div className="text-sm">
                    <div className="font-medium text-slate-800">{mdyDow(iso)}</div>
                    <div className="text-[11px] text-slate-500">{iso}</div>
                  </div>
                  <div className="flex gap-1">
                    {SLOTS.map((s) => {
                      const on = codes.has(s.code);
                      return (
                        <button
                          key={s.code}
                          onClick={() => toggle(iso, s.code)}
                          className={`w-9 h-9 rounded-md border text-xs font-bold uppercase tracking-widest transition-colors ${
                            on ? s.cls : "bg-white border-slate-200 text-slate-400 hover:border-slate-400"
                          }`}
                          data-testid={`admin-avail-toggle-${iso}-${s.code}`}
                          title={s.label}
                        >
                          {s.code}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="border-t border-slate-200 bg-slate-50/60 px-4 py-3 space-y-2" data-testid="admin-avail-counters">
          {assignedConflicts.length > 0 && (
            <div className="rounded-md border-2 border-red-400 bg-red-50 px-3 py-2 text-[12px] text-red-900 leading-tight" data-testid="admin-avail-assigned-blocks">
              <div className="font-black uppercase tracking-widest text-[10px] flex items-center gap-1">
                <AlertTriangle size={11} /> Already scheduled — heads-up
              </div>
              <div className="mt-1">
                {employee?.name || "This employee"} is already assigned a shift on{" "}
                <b>{assignedConflicts.map(b => mdy(b.date)).join(", ")}</b>.
                Employees can&apos;t self-submit availability that skips these dates — they&apos;d need a Time Off request. You can still override here.
              </div>
            </div>
          )}
          {noticeCautions.length > 0 && (
            <div className="rounded-md border-2 border-amber-400 bg-amber-50 px-3 py-2 text-[12px] text-amber-900 leading-tight" data-testid="admin-avail-notice-cautions">
              <div className="font-black uppercase tracking-widest text-[10px] flex items-center gap-1">
                <AlertTriangle size={11} /> {facilityId === "solano" ? "Less than 24 hours notice" : "Less than 14 days notice"}
              </div>
              <div className="mt-1 space-y-0.5">
                {noticeCautions.map((c) => (
                  <div key={c.date} data-testid={`admin-avail-caution-${c.date}`}>
                    <b>{mdy(c.date)}</b> is {c.daysAhead === 0 ? "today" : `${c.daysAhead} day${c.daysAhead === 1 ? "" : "s"} away`} — less than {facilityId === "solano" ? "24 hours" : "14 days"} notice. This may result in an occurrence.
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">
              Rule check
              {!anyRuleSet && (
                <span className="ml-2 normal-case tracking-normal text-slate-400 font-normal">
                  No minimums set for this facility
                </span>
              )}
            </div>
            {anyRuleSet && (
              shortRules.length === 0 ? (
                <span
                  data-testid="admin-avail-rules-ok"
                  className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-md border bg-emerald-50 border-emerald-300 text-emerald-800"
                >
                  <Check size={11} /> All minimums met
                </span>
              ) : (
                <span
                  data-testid="admin-avail-rules-short"
                  className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-md border bg-amber-50 border-amber-300 text-amber-800"
                >
                  <AlertTriangle size={11} /> {shortRules.length} rule{shortRules.length === 1 ? "" : "s"} short
                </span>
              )
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {RULES.map((r) => {
              const dim = r.need <= 0;
              const met = !dim && r.have >= r.need;
              const cls = dim
                ? "bg-white border-slate-200 text-slate-400"
                : met
                  ? "bg-emerald-50 border-emerald-300 text-emerald-800"
                  : "bg-amber-50 border-amber-300 text-amber-800";
              return (
                <span
                  key={r.key}
                  data-testid={`admin-avail-rule-${r.key}`}
                  className={`inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-widest px-2 py-1 rounded-md border tabular-nums ${cls}`}
                  title={dim ? `${r.label} — no minimum set` : `${r.label}: ${r.have}/${r.need}`}
                >
                  <span className="opacity-80">{r.label}</span>
                  <b>{r.have}{dim ? "" : `/${r.need}`}</b>
                  {!dim && (met
                    ? <Check size={10} className="opacity-80" />
                    : <AlertTriangle size={10} className="opacity-80" />)}
                </span>
              );
            })}
          </div>
          {shortRules.length > 0 && (
            <div className="text-[11px] text-amber-800 italic flex items-start gap-1">
              <AlertTriangle size={11} className="mt-0.5 shrink-0" />
              <span>
                Manager override — you can still save below the minimum. Employee is short on{" "}
                <b>{shortRules.map(r => `${r.label} (${r.have}/${r.need})`).join(", ")}</b>.
              </span>
            </div>
          )}
        </div>

        <div className="p-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-fuchsia-600 hover:bg-fuchsia-700" onClick={save} disabled={saving} data-testid="admin-avail-save">
            <Save size={14} className="mr-1.5" />
            {saving ? "Saving…" : "Save availability"}
          </Button>
        </div>
      </div>
    </div>
  );
}
